<!-- components/committeeScoreForm.vue -->
<!-- ปรับปรุง: 
  1. แก้ไข loadExistingData ให้ทำงานถูกต้อง (ใช้ this.$set เพื่อ reactivity)
  2. เพิ่ม max validation ตาม criteria
  3. emit score ที่คำนวณแล้วกลับ parent ให้ถูกต้อง
  4. เพิ่ม summary table แสดงรายละเอียดแต่ละกรรมการ
  5. แก้ไข bug ที่ loadCriteria ถูกเรียกก่อน mount
-->
<template>
  <div>
    <v-card class="pa-4 mb-4" outlined>
      <v-card-title class="text-center">
        <v-icon color="info" left>mdi-account-group</v-icon>
        <h3>
          คะแนนรายกรรมการ
          <span class="blue--text">(เกณฑ์ใหม่ ว19/2568)</span>
        </h3>
      </v-card-title>

      <div class="text-center mt-2 mb-3">
        ครั้งที่ {{ times }} —
        <span v-if="times <= 2"
          >2 ด้าน (ด้านวิชาชีพ + ด้านคุณลักษณะส่วนบุคคล)</span
        >
        <span v-else
          >3 ด้าน (ด้านวิชาชีพ + ด้านสังคม + ด้านคุณลักษณะส่วนบุคคล)</span
        >
      </div>

      <!-- แสดงเกณฑ์ขั้นต่ำ -->
      <v-alert type="info" dense outlined class="mx-4 mb-4">
        <div class="font-weight-bold mb-1">
          เกณฑ์ขั้นต่ำจำนวนรายการครั้งที่ {{ times }}:
        </div>
        <div v-for="rule in criteriaRules" :key="rule.aspect_code">
          {{ rule.aspect_name }}: ไม่น้อยกว่า
          <strong>{{ rule.min_items }}</strong> รายการ
          <span class="grey--text"
            >(สูงสุด {{ getMaxItems(rule.aspect_code) }} รายการ)</span
          >
        </div>
      </v-alert>

      <!-- กรรมการแต่ละคน -->
      <v-row v-for="(committee, idx) in committees" :key="idx" class="mx-2">
        <v-col cols="12">
          <v-card
            outlined
            class="pa-3"
            :class="getCommitteeCardClass(committee)"
          >
            <div class="d-flex align-center mb-2">
              <v-avatar
                size="32"
                :color="getAvatarColor(committee)"
                class="mr-2"
              >
                <span class="white--text font-weight-bold">{{ idx + 1 }}</span>
              </v-avatar>
              <h4>กรรมการคนที่ {{ idx + 1 }}</h4>
              <v-spacer></v-spacer>

              <!-- สถานะผ่าน/ไม่ผ่าน -->
              <v-chip
                v-if="committee.overall_pass === 'pass'"
                color="success"
                dark
                small
              >
                <v-icon small left>mdi-check-circle</v-icon> ผ่านทุกด้าน
              </v-chip>
              <v-chip
                v-else-if="committee.overall_pass === 'fail'"
                color="error"
                dark
                small
              >
                <v-icon small left>mdi-close-circle</v-icon> ไม่ผ่าน
                {{ getFailedAspects(committee) }}
              </v-chip>
              <v-chip v-else color="grey" outlined small>
                <v-icon small left>mdi-minus-circle-outline</v-icon>
                รอกรอกข้อมูล
              </v-chip>
            </div>

            <v-row dense>
              <!-- ชื่อกรรมการ -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="committee.committee_name"
                  label="ชื่อ-นามสกุล กรรมการ *"
                  prepend-icon="mdi-account"
                  outlined
                  dense
                  :rules="[v => !!v || 'กรุณากรอกชื่อกรรมการ']"
                  @input="validateCommittee(idx)"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="committee.committee_position"
                  label="ตำแหน่ง"
                  prepend-icon="mdi-badge-account"
                  outlined
                  dense
                ></v-text-field>
              </v-col>

              <!-- ด้านวิชาชีพ -->
              <v-col cols="12" :md="times <= 2 ? 6 : 4">
                <v-text-field
                  v-model.number="committee.score_profession"
                  type="number"
                  :label="
                    `ด้านวิชาชีพ (≥${getMinItems(
                      'profession'
                    )} จาก ${getMaxItems('profession')} รายการ) *`
                  "
                  prepend-icon="mdi-school"
                  outlined
                  dense
                  :min="0"
                  :max="getMaxItems('profession')"
                  :rules="professionRules"
                  :error="committee.pass_profession === 'fail'"
                  :success="committee.pass_profession === 'pass'"
                  :hint="getHint(committee.score_profession, 'profession')"
                  persistent-hint
                  @input="validateCommittee(idx)"
                ></v-text-field>
              </v-col>

              <!-- ด้านสังคม (ครั้งที่ 3-4 เท่านั้น) -->
              <v-col cols="12" md="4" v-if="times >= 3">
                <v-text-field
                  v-model.number="committee.score_social"
                  type="number"
                  :label="
                    `ด้านสังคม (≥${getMinItems('social')} จาก ${getMaxItems(
                      'social'
                    )} รายการ) *`
                  "
                  prepend-icon="mdi-account-multiple"
                  outlined
                  dense
                  :min="0"
                  :max="getMaxItems('social')"
                  :rules="socialRules"
                  :error="committee.pass_social === 'fail'"
                  :success="committee.pass_social === 'pass'"
                  :hint="getHint(committee.score_social, 'social')"
                  persistent-hint
                  @input="validateCommittee(idx)"
                ></v-text-field>
              </v-col>

              <!-- ด้านคุณลักษณะส่วนบุคคล -->
              <v-col cols="12" :md="times <= 2 ? 6 : 4">
                <v-text-field
                  v-model.number="committee.score_personality"
                  type="number"
                  :label="
                    `ด้านคุณลักษณะ (≥${getMinItems(
                      'personality'
                    )} จาก ${getMaxItems('personality')} รายการ) *`
                  "
                  prepend-icon="mdi-heart"
                  outlined
                  dense
                  :min="0"
                  :max="getMaxItems('personality')"
                  :rules="personalityRules"
                  :error="committee.pass_personality === 'fail'"
                  :success="committee.pass_personality === 'pass'"
                  :hint="getHint(committee.score_personality, 'personality')"
                  persistent-hint
                  @input="validateCommittee(idx)"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>

      <!-- สรุปผลรวม -->
      <v-card class="mx-4 mt-4 pa-3" :color="overallPassColor" dark>
        <div class="text-center">
          <h3>
            <v-icon left>{{
              overallPass ? "mdi-check-circle" : "mdi-alert-circle"
            }}</v-icon>
            ผลการประเมินครั้งที่ {{ times }}:
            {{
              allFilled
                ? overallPass
                  ? "ผ่าน (กรรมการทั้ง 3 คนผ่านทุกด้าน)"
                  : "ไม่ผ่าน"
                : "กรุณากรอกข้อมูลให้ครบ"
            }}
          </h3>
        </div>
      </v-card>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "CommitteeScoreForm",
  props: {
    times: {
      type: Number,
      required: true
    },
    existingData: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      ApiKey: "HRvec2021",
      criteriaRules: [],
      committees: [
        this.createEmptyCommittee(1),
        this.createEmptyCommittee(2),
        this.createEmptyCommittee(3)
      ]
    };
  },
  computed: {
    allFilled() {
      return this.committees.every(c => {
        const hasProf =
          c.score_profession !== null && c.score_profession !== "";
        const hasPers =
          c.score_personality !== null && c.score_personality !== "";
        const hasSocial =
          this.times < 3 || (c.score_social !== null && c.score_social !== "");
        const hasName = !!c.committee_name;
        return hasProf && hasPers && hasSocial && hasName;
      });
    },
    overallPass() {
      if (!this.allFilled) return false;
      return this.committees.every(c => c.overall_pass === "pass");
    },
    overallPassColor() {
      if (!this.allFilled) return "grey";
      return this.overallPass ? "success" : "error";
    },
    // === Validation Rules ===
    professionRules() {
      const max = this.getMaxItems("profession");
      return [
        v => (v !== "" && v !== null) || "กรุณากรอก",
        v => v >= 0 || "ต้องไม่น้อยกว่า 0",
        v => v <= max || `ไม่เกิน ${max} รายการ`
      ];
    },
    socialRules() {
      const max = this.getMaxItems("social");
      return [
        v => (v !== "" && v !== null) || "กรุณากรอก",
        v => v >= 0 || "ต้องไม่น้อยกว่า 0",
        v => v <= max || `ไม่เกิน ${max} รายการ`
      ];
    },
    personalityRules() {
      const max = this.getMaxItems("personality");
      return [
        v => (v !== "" && v !== null) || "กรุณากรอก",
        v => v >= 0 || "ต้องไม่น้อยกว่า 0",
        v => v <= max || `ไม่เกิน ${max} รายการ`
      ];
    }
  },
  watch: {
    times: {
      immediate: true,
      handler(val) {
        if (val) this.loadCriteria();
      }
    },
    existingData: {
      immediate: true,
      deep: true,
      handler(data) {
        if (data && data.length > 0) {
          this.loadExistingData(data);
        }
      }
    }
  },
  methods: {
    createEmptyCommittee(no) {
      return {
        committee_no: no,
        committee_name: "",
        committee_position: "",
        score_profession: null,
        score_social: null,
        score_personality: null,
        pass_profession: "pending",
        pass_social: "pending",
        pass_personality: "pending",
        overall_pass: "pending"
      };
    },

    async loadCriteria() {
      try {
        let result = await this.$http.post("assistant_teacher_criteria.php", {
          ApiKey: this.ApiKey,
          eval_times: this.times
        });
        this.criteriaRules = result.data || [];
      } catch (e) {
        console.error("Error loading criteria:", e);
        this.criteriaRules = [];
      }
    },

    loadExistingData(data) {
      for (let i = 0; i < 3; i++) {
        const existing = data.find(d => parseInt(d.committee_no) === i + 1);
        if (existing) {
          // ใช้ $set เพื่อให้ Vue reactivity ทำงาน
          this.$set(this.committees, i, {
            committee_no: i + 1,
            committee_name: existing.committee_name || "",
            committee_position: existing.committee_position || "",
            score_profession:
              existing.score_profession !== null &&
              existing.score_profession !== ""
                ? parseInt(existing.score_profession)
                : null,
            score_social:
              existing.score_social !== null && existing.score_social !== ""
                ? parseInt(existing.score_social)
                : null,
            score_personality:
              existing.score_personality !== null &&
              existing.score_personality !== ""
                ? parseInt(existing.score_personality)
                : null,
            pass_profession: existing.pass_profession || "pending",
            pass_social: existing.pass_social || "pending",
            pass_personality: existing.pass_personality || "pending",
            overall_pass: existing.overall_pass || "pending"
          });
          // validate ข้อมูลที่โหลดมา
          this.$nextTick(() => this.validateCommittee(i));
        }
      }
    },

    getMinItems(aspectCode) {
      const rule = this.criteriaRules.find(r => r.aspect_code === aspectCode);
      return rule ? parseInt(rule.min_items) : 0;
    },

    getMaxItems(aspectCode) {
      // จำนวนรายการสูงสุดตามด้าน
      const maxMap = {
        profession: 15,
        social: 3,
        personality: 16
      };
      return maxMap[aspectCode] || 20;
    },

    getHint(score, aspectCode) {
      if (score === null || score === "") return "";
      const min = this.getMinItems(aspectCode);
      if (min === 0) return "";
      return score >= min
        ? `✓ ผ่าน (${score}/${min})`
        : `✗ ไม่ผ่าน (${score}/${min})`;
    },

    validateCommittee(idx) {
      const c = this.committees[idx];

      // ด้านวิชาชีพ
      const minProf = this.getMinItems("profession");
      if (c.score_profession !== null && c.score_profession !== "") {
        c.pass_profession = c.score_profession >= minProf ? "pass" : "fail";
      } else {
        c.pass_profession = "pending";
      }

      // ด้านสังคม (ครั้งที่ 3-4)
      if (this.times >= 3) {
        const minSocial = this.getMinItems("social");
        if (c.score_social !== null && c.score_social !== "") {
          c.pass_social = c.score_social >= minSocial ? "pass" : "fail";
        } else {
          c.pass_social = "pending";
        }
      } else {
        c.pass_social = "pass"; // ครั้งที่ 1-2 ไม่มีด้านสังคม
      }

      // ด้านคุณลักษณะ
      const minPers = this.getMinItems("personality");
      if (c.score_personality !== null && c.score_personality !== "") {
        c.pass_personality = c.score_personality >= minPers ? "pass" : "fail";
      } else {
        c.pass_personality = "pending";
      }

      // overall
      if (
        c.pass_profession !== "pending" &&
        c.pass_social !== "pending" &&
        c.pass_personality !== "pending"
      ) {
        c.overall_pass =
          c.pass_profession === "pass" &&
          c.pass_social === "pass" &&
          c.pass_personality === "pass"
            ? "pass"
            : "fail";
      } else {
        c.overall_pass = "pending";
      }

      // force reactivity
      this.$set(this.committees, idx, { ...c });

      // emit ให้ parent component รู้
      this.$emit("score-changed", {
        committees: this.committees,
        overallPass: this.overallPass
      });
    },

    getAvatarColor(committee) {
      if (committee.overall_pass === "pass") return "success";
      if (committee.overall_pass === "fail") return "error";
      return "grey";
    },

    getCommitteeCardClass(committee) {
      if (committee.overall_pass === "pass") return "border-success";
      if (committee.overall_pass === "fail") return "border-error";
      return "";
    },

    getFailedAspects(committee) {
      const failed = [];
      if (committee.pass_profession === "fail") failed.push("วิชาชีพ");
      if (committee.pass_social === "fail") failed.push("สังคม");
      if (committee.pass_personality === "fail") failed.push("คุณลักษณะ");
      return failed.length > 0 ? `(${failed.join(", ")})` : "";
    },

    // เรียกจาก parent เพื่อดึงข้อมูลกรรมการไปบันทึก
    getCommitteeData() {
      return this.committees.map(c => ({
        committee_no: c.committee_no,
        committee_name: c.committee_name,
        committee_position: c.committee_position,
        score_profession: c.score_profession,
        score_social: this.times >= 3 ? c.score_social : null,
        score_personality: c.score_personality,
        pass_profession: c.pass_profession,
        pass_social: c.pass_social,
        pass_personality: c.pass_personality,
        overall_pass: c.overall_pass
      }));
    },

    // ตรวจสอบว่ากรอกครบหรือยัง
    isValid() {
      return this.committees.every(c => {
        const hasName = !!c.committee_name;
        const hasProf =
          c.score_profession !== null && c.score_profession !== "";
        const hasPers =
          c.score_personality !== null && c.score_personality !== "";
        const hasSocial =
          this.times < 3 || (c.score_social !== null && c.score_social !== "");
        return hasName && hasProf && hasPers && hasSocial;
      });
    },

    // reset ฟอร์ม
    reset() {
      this.committees = [
        this.createEmptyCommittee(1),
        this.createEmptyCommittee(2),
        this.createEmptyCommittee(3)
      ];
    }
  }
};
</script>

<style scoped>
.border-success {
  border-left: 4px solid #4caf50 !important;
}
.border-error {
  border-left: 4px solid #f44336 !important;
}
</style>